import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, CheckCircle2, Loader2, Clock, ShieldCheck, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_d23d8491-b3b1-4bd6-8f0e-4d1750125554/artifacts/uxopwla6_WhatsApp%20Image%202026-03-10%20at%204.20.57%20PM.jpeg";
const HEAD = "font-['Cabinet_Grotesk',_'Manrope',_sans-serif]";

const BENEFITS = [
  'Complete guest lifecycle management',
  'Automated rent & electricity tracking',
  'Daily Hisab, Ledger & financial reports',
  'Multi-user role-based access',
];

const BrandPanel = () => (
  <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-zinc-950 text-white p-12 flex-col justify-between">
    <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#002FA7]/40 blur-3xl" />
    <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-emerald-500/15 blur-3xl" />
    <div className="absolute inset-0 opacity-[0.06] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:48px_48px]" />

    <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="relative flex items-center gap-3 group [perspective:600px]" data-testid="request-logo-link" title="Go to home">
      <img src={LOGO_URL} alt="StayPoint" className="w-11 h-11 rounded-xl object-contain bg-white p-0.5 shadow-md transition-transform duration-300 group-hover:[transform:rotateY(22deg)_scale(1.08)] group-hover:shadow-xl" />
      <div className="leading-tight">
        <h1 className={`${HEAD} text-xl font-extrabold tracking-tight group-hover:text-white/80 transition-colors`}>StayPoint</h1>
        <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-semibold">PG Management</p>
      </div>
    </Link>

    <div className="relative max-w-md">
      <motion.span
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1.5 text-xs font-semibold mb-6"
      >
        <Sparkles className="w-3.5 h-3.5" /> Built by Owners · Trusted by Managers
      </motion.span>
      <motion.h2
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
        className={`${HEAD} text-4xl xl:text-5xl font-black tracking-tight leading-[1.05] mb-5`}
      >
        Get started with<br /><span className="text-[#5B8DEF]">StayPoint</span>
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
        className="text-white/60 text-lg leading-relaxed mb-8"
      >
        Tell us about your property and our team will set up your account — usually within 24 hours.
      </motion.p>

      <ul className="space-y-3.5">
        {BENEFITS.map((b, i) => (
          <motion.li
            key={b}
            initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.15 + i * 0.08 }}
            className="flex items-center gap-3 text-white/85"
          >
            <span className="w-6 h-6 rounded-full bg-emerald-400/15 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-4 h-4 text-emerald-300" />
            </span>
            {b}
          </motion.li>
        ))}
      </ul>
    </div>

    <div className="relative flex items-center gap-5 text-xs text-white/40">
      <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-emerald-400" /> Setup in 24 hours</span>
      <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Data fully isolated</span>
    </div>
  </div>
);

export const RequestAccess = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    organisation_name: '', owner_name: '', mobile: '', email: '', city: '', capacity: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, capacity: parseInt(form.capacity) || 0 };
      await axios.post(`${API}/organisations/request-access`, payload);
      setSubmitted(true);
      toast.success('Request submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-4 font-['Outfit',_'IBM_Plex_Sans',_sans-serif]" data-testid="request-success">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,47,167,0.08),transparent_60%)]" />
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
          className="relative w-full max-w-md rounded-3xl bg-white border border-zinc-200 shadow-[0_30px_70px_-30px_rgba(0,47,167,0.3)] p-10 text-center"
        >
          <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-5">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className={`${HEAD} text-2xl font-bold text-zinc-950 mb-2`}>Request Submitted!</h2>
          <p className="text-zinc-600 mb-7">
            Thank you for your interest in StayPoint. Our team will review your request and contact you at <strong className="text-zinc-900">{form.email}</strong> with your login credentials once approved.
          </p>
          <Link to="/login">
            <Button className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-xl h-11 font-semibold">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Login
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  const inputCls = "rounded-xl h-11 border-zinc-200 focus-visible:ring-[#002FA7]";

  return (
    <div className="min-h-screen flex bg-[#FAFAFA] font-['Outfit',_'IBM_Plex_Sans',_sans-serif]" data-testid="request-access-page">
      <BrandPanel />

      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-6 sm:p-10 relative">
        <div className="absolute inset-0 lg:hidden bg-[radial-gradient(ellipse_at_top,rgba(0,47,167,0.08),transparent_60%)]" />
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="relative w-full max-w-md"
        >
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img src={LOGO_URL} alt="StayPoint" className="w-10 h-10 rounded-xl object-contain ring-1 ring-zinc-200" />
            <h1 className={`${HEAD} text-xl font-extrabold text-zinc-950`}>StayPoint</h1>
          </div>

          <div className="rounded-3xl bg-white border border-zinc-200 shadow-[0_30px_70px_-30px_rgba(0,47,167,0.3)] p-8 sm:p-10">
            <Link to="/login" className="text-[#002FA7] hover:text-[#00227A] text-sm font-medium inline-flex items-center mb-5">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Login
            </Link>
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[#002FA7] mb-2">Get started</p>
            <h2 className={`${HEAD} text-3xl font-bold tracking-tight text-zinc-950 mb-1.5`}>Request access</h2>
            <p className="text-zinc-500 mb-7">Fill in your organisation details to get started.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="organisation_name" className="text-zinc-700 font-medium">Organisation Name *</Label>
                <Input id="organisation_name" type="text" placeholder="e.g. Stay Point Private Limited"
                  value={form.organisation_name}
                  onChange={(e) => setForm({ ...form, organisation_name: e.target.value.toUpperCase() })}
                  required data-testid="org-name-input" style={{ textTransform: 'uppercase' }} className={inputCls} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="owner_name" className="text-zinc-700 font-medium">Owner / Contact Name *</Label>
                <Input id="owner_name" type="text" placeholder="Your full name"
                  value={form.owner_name}
                  onChange={(e) => setForm({ ...form, owner_name: e.target.value.toUpperCase() })}
                  required data-testid="owner-name-input" style={{ textTransform: 'uppercase' }} className={inputCls} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="mobile" className="text-zinc-700 font-medium">Mobile Number *</Label>
                  <Input id="mobile" type="tel" placeholder="10-digit number"
                    value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                    required data-testid="mobile-input" className={inputCls} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="capacity" className="text-zinc-700 font-medium">Number of Beds *</Label>
                  <Input id="capacity" type="number" placeholder="Total beds" min="1"
                    value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                    required data-testid="capacity-input" className={inputCls} />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-zinc-700 font-medium">Email Address *</Label>
                <Input id="email" type="email" placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
                  required data-testid="email-input" style={{ textTransform: 'lowercase' }} className={inputCls} />
                <p className="text-xs text-zinc-400">This will be your login email</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="city" className="text-zinc-700 font-medium">City *</Label>
                <Input id="city" type="text" placeholder="City name"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value.toUpperCase() })}
                  required data-testid="city-input" style={{ textTransform: 'uppercase' }} className={inputCls} />
              </div>

              <Button type="submit" disabled={loading} data-testid="submit-request-btn"
                className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white rounded-xl h-12 text-base font-semibold shadow-[0_12px_28px_-8px_rgba(0,47,167,0.5)] group mt-1 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_36px_-8px_rgba(0,47,167,0.6)] active:translate-y-0 active:shadow-[0_6px_14px_-4px_rgba(0,47,167,0.5)]">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <>Submit Request <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" /></>}
              </Button>
            </form>
          </div>

          <p className="mt-6 text-xs text-zinc-400 text-center">
            <Link to="/" className="hover:text-[#002FA7]">← Back to home</Link>
            <span className="mx-2">·</span>
            <Link to="/privacy" className="hover:text-[#002FA7]">Privacy</Link>
            <span className="mx-2">·</span>
            <Link to="/terms" className="hover:text-[#002FA7]">Terms</Link>
            <span className="mx-2">·</span>
            © {new Date().getFullYear()} Stay Point Private Limited
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RequestAccess;
